import React, { useEffect, useCallback, useState } from 'react'
import axios from 'axios'
import { connect } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { Dispatch, bindActionCreators } from 'redux'
import {
  Axis,
  CrmScoringState,
  CrmScoringAction,
  setAxis,
  setDateRange,
  setMatrixLoading,
  setMatrix,
  toggleCell,
  clearSelectedCells,
  setUsersLoading,
  setUsers,
  setUsersPage,
  setUsersPerPage,
  setSort,
  setSnapshotDate,
  setSearchQuery,
  setCheckedUsers,
  SortBy,
  SortOrder,
} from '../actions/crmScoring'
import { FETCH_KEYS, fetchCrmScoringExportCsv, readCookie } from '../api'
import CrmScoringPage from '../components/CrmScoring/CrmScoringPage'

interface OwnProps {
  client: { id: number }
  axis: Axis
}

interface StateProps {
  crmScoring: CrmScoringState
}

interface DispatchProps {
  setAxis: typeof setAxis
  setDateRange: typeof setDateRange
  setMatrixLoading: typeof setMatrixLoading
  setMatrix: typeof setMatrix
  toggleCell: typeof toggleCell
  clearSelectedCells: typeof clearSelectedCells
  setUsersLoading: typeof setUsersLoading
  setUsers: typeof setUsers
  setUsersPage: typeof setUsersPage
  setUsersPerPage: typeof setUsersPerPage
  setSort: typeof setSort
  setSnapshotDate: typeof setSnapshotDate
  setSearchQuery: typeof setSearchQuery
  setCheckedUsers: typeof setCheckedUsers
}

type Props = OwnProps & StateProps & DispatchProps

const CrmScoringContainer: React.FC<Props> = (props) => {
  const { client, axis, crmScoring: state } = props
  const navigate = useNavigate()
  const { urlName } = useParams()

  // axis が URL から渡された値と異なれば Redux に反映
  useEffect(() => {
    if (state.axis !== axis) {
      props.setAxis(axis)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [axis])

  const authHeader = () => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${readCookie('access_token')}`,
  })

  const loadMatrix = useCallback(async () => {
    props.setMatrixLoading(true)
    try {
      const url = FETCH_KEYS.fetchCrmScoringMatrix(
        client.id, axis, state.dateRange.start, state.dateRange.end, state.snapshotDate,
      )
      const res = await axios.get(url, { headers: authHeader() })
      props.setMatrix(res.data.total, res.data.cells)
    } finally {
      props.setMatrixLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client.id, axis, state.dateRange.start, state.dateRange.end, state.snapshotDate])

  const loadUsers = useCallback(async () => {
    props.setUsersLoading(true)
    try {
      const url = FETCH_KEYS.fetchCrmScoringUsers(
        client.id, axis, state.dateRange.start, state.dateRange.end, state.selectedCells,
        state.users.page, state.users.perPage, state.sort.sortBy, state.sort.sortOrder, state.snapshotDate,
        state.searchQuery,
      )
      const res = await axios.get(url, { headers: authHeader() })
      props.setUsers(res.data.users, res.data.total, res.data.page, res.data.per_page)
    } finally {
      props.setUsersLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client.id, axis, state.dateRange.start, state.dateRange.end, state.selectedCells, state.users.page, state.users.perPage, state.sort.sortBy, state.sort.sortOrder, state.snapshotDate, state.searchQuery])

  useEffect(() => {
    loadMatrix()
  }, [loadMatrix])

  useEffect(() => {
    loadUsers()
  }, [loadUsers])

  const [csvLoading, setCsvLoading] = useState(false)
  const onCsvDownload = async () => {
    if (csvLoading) return
    setCsvLoading(true)
    try {
      const ids = state.checkedUserIds.length > 0 ? state.checkedUserIds : null
      const url = await fetchCrmScoringExportCsv(
        client.id, axis, state.dateRange.start, state.dateRange.end, state.selectedCells, ids, state.snapshotDate,
        state.sort.sortBy, state.sort.sortOrder, state.searchQuery,
      )
      window.location.href = url
    } finally {
      setCsvLoading(false)
    }
  }

  const onAxisChange = (next: Axis) => {
    if (next === axis) return
    const path = next === 'f2_m2' ? 'influence' : 'active'
    navigate(`/admin/clients/${urlName}/crm-scoring/${path}`)
  }

  return (
    <CrmScoringPage
      clientId={client.id}
      axis={axis}
      dateRange={state.dateRange}
      matrix={state.matrix}
      selectedCells={state.selectedCells}
      users={state.users}
      sort={state.sort}
      snapshotDate={state.snapshotDate}
      searchQuery={state.searchQuery}
      checkedUserIds={state.checkedUserIds}
      onAxisChange={onAxisChange}
      onDateRangeChange={(s, e) => props.setDateRange(s, e)}
      onToggleCell={(x, y) => props.toggleCell(x, y)}
      onClearSelectedCells={() => props.clearSelectedCells()}
      onSearchQueryChange={(q) => props.setSearchQuery(q)}
      onSetCheckedUsers={(ids) => props.setCheckedUsers(ids)}
      onPageChange={(p) => props.setUsersPage(p)}
      onPerPageChange={(p) => props.setUsersPerPage(p)}
      onSortChange={(by: SortBy, order: SortOrder) => props.setSort(by, order)}
      onSnapshotDateChange={(d: string) => props.setSnapshotDate(d)}
      onCsvDownload={onCsvDownload}
      csvLoading={csvLoading}
    />
  )
}

const mapStateToProps = (state: { crmScoring: CrmScoringState }): StateProps => ({
  crmScoring: state.crmScoring,
})

const mapDispatchToProps = (dispatch: Dispatch<CrmScoringAction>): DispatchProps =>
  bindActionCreators(
    {
      setAxis,
      setDateRange,
      setMatrixLoading,
      setMatrix,
      toggleCell,
      clearSelectedCells,
      setUsersLoading,
      setUsers,
      setUsersPage,
      setUsersPerPage,
      setSort,
      setSnapshotDate,
      setSearchQuery,
      setCheckedUsers,
    },
    dispatch,
  )

export default connect(mapStateToProps, mapDispatchToProps)(CrmScoringContainer)
